import React, { useCallback, useRef, useState } from 'react';
import { motion, AnimatePresence, useSpring, useTransform } from 'motion/react';
import { Heart } from 'lucide-react';
import { useYapInteractions, type PostType } from '../YapInteractionsContext';

/**
 * LikeButton — Animated heart button with spring bounce and particle burst.
 *
 * On like:   heart scales 1.0 → 1.4 → 1.0 with spring { damping: 8, stiffness: 200 }
 *            simultaneously fills with liked color
 *            tiny heart particles scatter outward and fade
 *
 * On unlike: softer reverse pulse 1.0 → 1.15 → 1.0, fill drains away
 */

interface LikeButtonProps {
  /** The post/yap/shout ID to like */
  postId: string;
  /** The type of post */
  postType: PostType;
  /** Original like count from server data */
  originalLikeCount: number;
  /** Whether this button should be disabled (e.g. can't interact) */
  disabled?: boolean;
  /** Custom class name for the wrapper */
  className?: string;
  /** Size of the heart icon in pixels (default 16 = w-4 h-4) */
  iconSize?: number;
  /** onClick handler called BEFORE the like toggle (for e.stopPropagation etc.) */
  onBeforeToggle?: (e: React.MouseEvent) => void;
  /** Optional extra validation — return false to prevent the toggle */
  canInteract?: () => boolean;
  /** Optional message to show when canInteract returns false */
  onBlockedInteraction?: () => void;
  /** Optional override for whether the item is liked (bypasses context) */
  isLikedOverride?: boolean;
  /** Optional override for the display count (bypasses context) */
  displayCountOverride?: number;
  /** Optional override for the toggle action (bypasses context) */
  onToggleOverride?: () => void;
}

// Particle positions: angle in degrees, distance in px
const PARTICLES = [
  { angle: -30, distance: 22 },
  { angle: -75, distance: 18 },
  { angle: -120, distance: 24 },
  { angle: -150, distance: 20 },
  { angle: 30, distance: 20 },
];

function LikeParticles({ show }: { show: boolean }) {
  return (
    <AnimatePresence>
      {show &&
        PARTICLES.map((p, i) => {
          const rad = (p.angle * Math.PI) / 180;
          const tx = Math.cos(rad) * p.distance;
          const ty = Math.sin(rad) * p.distance;
          return (
            <motion.span
              key={i}
              initial={{ opacity: 1, scale: 0.6, x: 0, y: 0 }}
              animate={{
                opacity: 0,
                scale: 0,
                x: tx,
                y: ty,
              }}
              exit={{ opacity: 0 }}
              transition={{
                duration: 0.45,
                ease: 'easeOut',
                delay: i * 0.03,
              }}
              className="absolute pointer-events-none"
              style={{
                top: '50%',
                left: '50%',
                marginTop: -3,
                marginLeft: -3,
              }}
            >
              <Heart
                className="fill-[#2d1ef1] text-[#2d1ef1]"
                style={{ width: 6, height: 6 }}
              />
            </motion.span>
          );
        })}
    </AnimatePresence>
  );
}

export function LikeButton({
  postId,
  postType,
  originalLikeCount,
  disabled = false,
  className = '',
  iconSize = 16,
  onBeforeToggle,
  canInteract,
  onBlockedInteraction,
  isLikedOverride,
  displayCountOverride,
  onToggleOverride,
}: LikeButtonProps) {
  const { toggleLike, isPostLiked, getPostLikeCount } = useYapInteractions();
  
  const liked = isLikedOverride !== undefined ? isLikedOverride : isPostLiked(postId, postType);
  const displayCount = displayCountOverride !== undefined ? displayCountOverride : getPostLikeCount(originalLikeCount, postId, postType);
  const [showParticles, setShowParticles] = useState(false);
  const particleTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  // Momentary blue flash on tap; settles back to the resting red color
  const [tapFlash, setTapFlash] = useState(false);
  const flashTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onBeforeToggle?.(e);

      if (disabled) return;
      if (canInteract && !canInteract()) {
        onBlockedInteraction?.();
        return;
      }

      const willLike = !liked;

      // Momentary blue flash on tap, then settle back to red
      if (flashTimeoutRef.current) clearTimeout(flashTimeoutRef.current);
      setTapFlash(true);
      flashTimeoutRef.current = setTimeout(() => setTapFlash(false), 350);

      // Fire particle burst on like
      if (willLike) {
        // Clear any pending timeout so rapid taps don't leave stale particles
        if (particleTimeoutRef.current) clearTimeout(particleTimeoutRef.current);
        setShowParticles(true);
        particleTimeoutRef.current = setTimeout(() => setShowParticles(false), 500);
      }

      if (onToggleOverride) {
        onToggleOverride();
      } else {
        toggleLike(postId, postType);
      }
    },
    [liked, disabled, postId, postType, toggleLike, canInteract, onBlockedInteraction, onBeforeToggle, onToggleOverride]
  );

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      className={`flex items-center gap-2 h-auto px-0 bg-transparent border-none outline-none ${
        disabled
          ? 'opacity-50 cursor-default'
          : tapFlash
          ? 'text-[#2d1ef1]'
          : liked
          ? 'text-[#E74C3C]'
          : 'text-[#666666] dark:text-[#E5E5E5] hover:text-[#E74C3C]'
      } ${className}`}
    >
      <span className="relative inline-flex items-center justify-center">
        <motion.span
          animate={
            liked
              ? { scale: [1, 1.4, 1] }
              : { scale: [1, 1.15, 1] }
          }
          transition={
            liked
              ? {
                  type: 'spring',
                  damping: 8,
                  stiffness: 200,
                  duration: 0.4,
                }
              : {
                  type: 'spring',
                  damping: 14,
                  stiffness: 200,
                  duration: 0.25,
                }
          }
          key={liked ? 'liked' : 'unliked'}
          className="inline-flex items-center justify-center"
        >
          <Heart
            style={{ width: iconSize, height: iconSize }}
            className={`transition-colors duration-150 ${
              tapFlash
                ? 'fill-[#2d1ef1]'
                : liked
                ? 'fill-[#E74C3C]'
                : ''
            }`}
          />
        </motion.span>
        <LikeParticles show={showParticles} />
      </span>
      <span className="text-sm font-medium">{displayCount}</span>
    </button>
  );
}
